import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes } from 'react';

export interface SwitchProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size'
> {
  size?: 'sm' | 'md' | 'lg';
  label?: string;
}

const sizeStyles: Record<
  string,
  { track: string; thumb: string; translate: string }
> = {
  sm: {
    track: 'w-8 h-[18px]',
    thumb: 'w-3.5 h-3.5',
    translate: 'translate-x-3.5',
  },
  md: {
    track: 'w-10 h-[22px]',
    thumb: 'w-[18px] h-[18px]',
    translate: 'translate-x-[18px]',
  },
  // 48x26 track with a 20px knob — the switch size the design system's
  // form sheets use (Figma node 7565-31974 Date & Time / All-day).
  lg: {
    track: 'w-12 h-[26px]',
    thumb: 'w-5 h-5',
    translate: 'translate-x-6',
  },
};

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  (
    { size = 'md', label, checked, disabled, className = '', id, ...props },
    ref,
  ) => {
    const generatedId = useId();
    const switchId = id ?? generatedId;
    const styles = sizeStyles[size];

    return (
      <div
        className={['inline-flex items-center gap-2', className]
          .filter(Boolean)
          .join(' ')}
      >
        <label
          htmlFor={switchId}
          className={[
            'relative inline-flex items-center shrink-0 cursor-pointer',
            disabled ? 'cursor-not-allowed opacity-50' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <input
            ref={ref}
            type="checkbox"
            role="switch"
            id={switchId}
            checked={checked}
            disabled={disabled}
            className="sr-only peer"
            {...props}
          />
          <div
            className={[
              'rounded-full transition-colors duration-150',
              styles.track,
              'bg-border-strong peer-checked:bg-primary',
              'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary',
              'peer-disabled:opacity-50 peer-disabled:cursor-not-allowed',
            ].join(' ')}
          />
          <div
            className={[
              'absolute left-0.5 top-1/2 -translate-y-1/2 rounded-full bg-white shadow-sm',
              'transition-transform duration-150',
              styles.thumb,
              checked ? styles.translate : 'translate-x-0',
            ].join(' ')}
          />
        </label>
        {label && (
          <label
            htmlFor={switchId}
            className={[
              'text-sm text-foreground select-none',
              disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            ].join(' ')}
          >
            {label}
          </label>
        )}
      </div>
    );
  },
);

Switch.displayName = 'Switch';
